import { useMemo } from 'react';
import moment from 'moment';
import { FINANCE_LOG_HEADERS } from '@trnsact/trnsact-shared-types';
import { FinanceTableColumn } from '../types';
import { formatCurrency, formatPercentage } from 'utils';

const formatMoneyValue = (value: number | null): string => {
  if (value === null || value === undefined) return '-';
  return formatCurrency(value.toString());
};

const formatDateValue = (value: string | null): string => (value ? moment(value).format('MM/DD/YYYY') : '-');

type FinanceColumnKey = FinanceTableColumn['renderKey'];

const columnSortKeys: Partial<Record<FinanceColumnKey, string>> = {
  locationBranch: 'location',
  salesRep: 'salesRep',
  financeMgr: 'financeMgr',
  dealDate: 'dealDate',
  estimatedCloseDate: 'estimatedCloseDate',
  createdDate: 'createdDate',
};

const columnFormatters: Partial<Record<FinanceColumnKey, NonNullable<FinanceTableColumn['formatter']>>> = {
  dealDate: row => formatDateValue(row.dealDate),
  estimatedCloseDate: row => formatDateValue(row.estimatedCloseDate),
  createdDate: row => formatDateValue(row.createdDate),
  dealAmount: row => formatMoneyValue(row.dealAmount),
  equipmentSalePrice: row => formatMoneyValue(row.equipmentSalePrice),
  equipmentCost: row => formatMoneyValue(row.equipmentCost),
  equipmentGross: row => formatMoneyValue(row.equipmentGross),
  equipmentMarginPercent: row => formatPercentage(row.equipmentMarginPercent),
  financeGross: row => formatMoneyValue(row.financeGross),
  financeProfit: row => formatMoneyValue(row.financeProfit),
  financeMarginPercent: row => formatPercentage(row.financeMarginPercent),
  escGross: row => formatMoneyValue(row.escGross),
  escCost: row => formatMoneyValue(row.escCost),
  escProfit: row => formatMoneyValue(row.escProfit),
  gapGross: row => formatMoneyValue(row.gapGross),
  gapCost: row => formatMoneyValue(row.gapCost),
  gapProfit: row => formatMoneyValue(row.gapProfit),
  insuranceGross: row => formatMoneyValue(row.insuranceGross),
  insuranceCost: row => formatMoneyValue(row.insuranceCost),
  insuranceProfit: row => formatMoneyValue(row.insuranceProfit),
  roadsideGross: row => formatMoneyValue(row.roadsideGross),
  roadsideCost: row => formatMoneyValue(row.roadsideCost),
  roadsideProfit: row => formatMoneyValue(row.roadsideProfit),
};

const toRenderKey = (key: (typeof FINANCE_LOG_HEADERS)[number]['key']): FinanceColumnKey => key as FinanceColumnKey;

export const useFinanceListColumns = () => {
  const columns: FinanceTableColumn[] = useMemo(() => {
    return FINANCE_LOG_HEADERS.map(({ key, header }) => {
      const renderKey = toRenderKey(key);

      return {
        header,
        renderKey,
        sortKey: columnSortKeys[renderKey],
        formatter: columnFormatters[renderKey],
      };
    });
  }, []);

  return { columns };
};
